import { useAuthentication, useQueryParam } from '@psyomics/ui-common';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { fetchVerificationStatus } from '../../../features/verification/verification-client';

type VerificationStatus = {
  hasEmail: boolean | null;
  isAccountVerified: boolean | null;
  error: Error | null;
};

const useVerificationStatus = ({
  apiUrl,
  isAuthenticated,
  isLoading,
  isVerificationRoute,
}: {
  apiUrl: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  isVerificationRoute: boolean;
}): VerificationStatus => {
  const [isAccountVerified, setIsAccountVerified] = useState<boolean | null>(null);
  const [hasEmail, setHasEmail] = useState<boolean | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    setIsAccountVerified(null);
    setHasEmail(null);

    fetchVerificationStatus(apiUrl)
      .then((response) => {
        setIsAccountVerified(response.isAccountVerified);
        setHasEmail(response.hasEmail);
      })
      .catch((err) => setError(new Error(String(err))));
  }, [apiUrl, isAuthenticated, isLoading, isVerificationRoute]);

  return { isAccountVerified, hasEmail, error };
};

const getSetupUrl = (hasEmail: boolean | null, redirect: string) => {
  const params = new URLSearchParams({ redirect });
  params.append('method', hasEmail ? 'email' : 'phone');

  return `/account-verification?${params.toString()}`;
};

const getRedirect = ({
  hasEmail,
  isAccountVerified,
  isAuthenticated,
  isLoading,
  isVerificationRoute,
  pathname,
  redirectParam,
}: {
  hasEmail: boolean | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAccountVerified: boolean | null;
  isVerificationRoute: boolean;
  pathname: string;
  redirectParam?: string;
}) => {
  if (!isAuthenticated || isLoading || isAccountVerified === null) return null;
  if (isAccountVerified && isVerificationRoute) return redirectParam ?? pathname;
  if (!isAccountVerified && !isVerificationRoute) return getSetupUrl(hasEmail, pathname);
  return null;
};

const RequireVerification = () => {
  const { apiUrl, isAuthenticated, isLoading } = useAuthentication();
  const { pathname } = useLocation();
  const redirectParam = useQueryParam('redirect');
  const isVerificationRoute = pathname === '/account-verification';
  const { isAccountVerified, hasEmail, error } = useVerificationStatus({
    apiUrl,
    isAuthenticated,
    isLoading,
    isVerificationRoute,
  });
  const isPending = !isAuthenticated || isLoading || isAccountVerified === null;
  const redirectTo = useMemo(() => {
    if (error) return null;
    const redirect = getRedirect({
      hasEmail,
      isAccountVerified,
      isAuthenticated,
      isLoading,
      isVerificationRoute,
      pathname,
      redirectParam,
    });
    return redirect;
  }, [error, isAuthenticated, hasEmail, isLoading, redirectParam, isVerificationRoute, isAccountVerified, pathname]);

  if (error) throw error;
  if (isPending) return null;
  if (redirectTo) {
    return <Navigate replace to={redirectTo} />;
  }
  return <Outlet />;
};

export { RequireVerification };
